import moment from 'moment';
import { createFilterOptions } from '@mui/material';

import { populateItemsResponse } from '../../../common/ItemTools';
import reviewedFact from '../stats';

// Generic helpers shared across the hero pages (item, author, and future
// list/site pages). Entity-specific helpers live in each hero page's own
// utils module.

const filterOptions = createFilterOptions();

// A duration for a facts line: minutes under the hour, whole hours above it
// (e.g. `45 minutes`, `6 hours`). Rounded rather than exact - the figure is a
// sense of scale.
export function humanizeMinutes(minutes) {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

export function formatDate(unixSeconds, dateOnly) {
  if (!unixSeconds) return '';
  const m = dateOnly ? moment.utc(unixSeconds * 1000) : moment(unixSeconds * 1000);
  return m.format('MMM D, YYYY');
}

/**
 * The facts for one namespace's copy of an author, list or site, as `{ text, title }`
 * segments with empty ones dropped - `title` is the segment's tooltip. `itemsCount` is
 * the live count for the namespace on show, and only that namespace's figures are
 * quoted - the reviewed share is private only.
 */
export function factsLine(data, itemsCount, namespace) {
  const stats = (data && data.stats) || {};
  const totalItems = itemsCount === undefined ? (stats.totalItems || 0) : itemsCount;
  const [dateVerb, time] = namespace === 'public'
    ? ['shared', data && data.timeShared]
    : ['created', data && data.timeCreated];

  return [
    { text: totalItems ? humanizeMinutes(stats.timeNeeded) : '' },
    { text: namespace === 'private' ? reviewedFact(stats) : '' },
    {
      text: time ? `${dateVerb} ${moment(time * 1000).format('MMM YYYY')}` : '',
      title: formatDate(time),
    },
  ].filter((fact) => fact.text);
}

// Populate a raw items response (e.g. an author/list preview) so the items are
// card-ready, returning the items and the total count.
export function buildItemsPreview(response) {
  const { items = [], itemsCount = 0 } = response || {};
  populateItemsResponse(items, response);
  return { items, itemsCount };
}

// Append a synthetic "Add <x>" entry so free-typed values can be created, the
// same affordance the hero edit forms use for tags and authors.
export function withCreateOption(options, params) {
  const next = filterOptions(options, params);
  if (params.inputValue) {
    next.push({ inputValue: params.inputValue, name: `Add "${params.inputValue}"` });
  }
  return next;
}

export function optionLabel(option) {
  return typeof option === 'string' ? option : (option.name || '');
}

export function formatReviewed(unixSeconds) {
  if (!unixSeconds) return '';
  return moment(unixSeconds * 1000).format('MMM D, YYYY, h:mm A');
}

export function dateToInput(unixSeconds, dateOnly) {
  if (!unixSeconds) return '';
  const m = dateOnly ? moment.utc(unixSeconds * 1000) : moment(unixSeconds * 1000);
  return m.format('YYYY-MM-DD');
}

export function dateTimeToInput(unixSeconds) {
  if (!unixSeconds) return '';
  return moment(unixSeconds * 1000).format('YYYY-MM-DDTHH:mm');
}

export function inputDateToUnix(value, dateOnly) {
  if (!value) return null;
  const [y, mo, d] = value.split('-').map(Number);
  if (!y || !mo || !d) return null;
  const ms = dateOnly ? Date.UTC(y, mo - 1, d) : new Date(y, mo - 1, d).getTime();
  return Math.round(ms / 1000);
}

export function inputDateTimeToUnix(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : Math.round(ms / 1000);
}

export function timestampToInput(unixSeconds, dateOnly) {
  return dateOnly ? dateToInput(unixSeconds, true) : dateTimeToInput(unixSeconds);
}

export function inputToTimestamp(value, dateOnly) {
  return dateOnly ? inputDateToUnix(value, true) : inputDateTimeToUnix(value);
}

export function normalizeTag(value) {
  return (value || '').trim().toLowerCase().replace(/\s+/g, '-');
}

// Href for following a URL typed into an edit box: trimmed, with https
// defaulted for scheme-less input so the browser doesn't resolve it against
// the app's own origin.
export function followableUrl(value) {
  const url = (value || '').trim();
  if (!url) return null;
  return /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
}

export function openUrlInNewTab(url) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}
